import { TableColumn, TableColumnOption } from '../../interfaces'

export const YEAR_PRIMARY_BOUNDARY = 1960

const toYearNumber = (value: string | number): number => Number(value)

export const flattenYearOptions = (
  columns: Array<TableColumn>
): Array<TableColumnOption> => {
  const options: TableColumnOption[] = []

  columns.forEach((col) => {
    col.options?.forEach((option) => {
      options.push({
        ...option,
        typeId: option.typeId ?? col.id,
        typeName: option.typeName ?? col.header,
      })
    })
  })

  return options.sort((a, b) => toYearNumber(b.value) - toYearNumber(a.value))
}

export const hasYearsBeforePrimaryBoundary = (
  columns: Array<TableColumn>
): boolean => {
  const options = flattenYearOptions(columns)
  if (options.length === 0) return false
  const minYear = Math.min(...options.map((o) => toYearNumber(o.value)))
  return minYear < YEAR_PRIMARY_BOUNDARY
}

export const splitYearOptions = (
  columns: Array<TableColumn>
): {
  primaryYears: Array<TableColumnOption>
  extendedYears: Array<TableColumnOption>
} => {
  const allYears = flattenYearOptions(columns)
  const primaryYears = allYears.filter(
    (o) => toYearNumber(o.value) >= YEAR_PRIMARY_BOUNDARY
  )
  const extendedYears = allYears.filter(
    (o) => toYearNumber(o.value) < YEAR_PRIMARY_BOUNDARY
  )

  return { primaryYears, extendedYears }
}

export const getCurrentCalendarYear = (): number => new Date().getFullYear()

export const groupYearsIntoDecadeColumns = (
  years: Array<TableColumnOption>
): Array<TableColumn> => {
  if (years.length === 0) return []

  const minYear = Math.min(...years.map((o) => toYearNumber(o.value)))
  const maxYear = Math.max(...years.map((o) => toYearNumber(o.value)))
  const numBuckets = Math.floor((maxYear - minYear) / 10) + 1

  const cols: Array<TableColumn> = Array.from(
    { length: numBuckets },
    (_, i) => ({
      id: i,
      header: '',
      options: [],
    })
  )

  years.forEach((option) => {
    const year = toYearNumber(option.value)
    const bucketIndex = Math.floor((year - minYear) / 10)
    if (bucketIndex >= 0 && bucketIndex < cols.length) {
      cols[bucketIndex].options?.push(option)
    }
  })

  cols.forEach((col) => {
    col.options?.sort((a, b) => toYearNumber(b.value) - toYearNumber(a.value))
  })

  return cols.filter((col) => (col.options?.length ?? 0) > 0).reverse()
}

export const getYearRangeLabel = (years: Array<TableColumnOption>): string => {
  if (years.length === 0) return ''
  const yearNumbers = years.map((o) => toYearNumber(o.value))
  return `${Math.max(...yearNumbers)} - ${Math.min(...yearNumbers)}`
}
